import { useState } from "react";
import { useAuthContext } from "./useAuthContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

export const useFormBuilder = () => {
    const { user } = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const generateForm = async (keyword, designPreferences) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/forms`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user}`,
                },
                body: JSON.stringify({ keyword, designPreferences }),
            });
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                setLoading(false);
                // Monthly AI-generation limit reached → trigger the soft-block upgrade modal.
                if (response.status === 429 && json.code === "LIMIT_AI_GENERATIONS") {
                    window.dispatchEvent(new CustomEvent("cfai:usage-limit", { detail: json }));
                }
                return null;
            }
            setLoading(false);
            // An AI generation was just consumed — let usage-driven UI (e.g. the BYOK
            // nudge banner) re-check eligibility without a reload.
            window.dispatchEvent(new Event("cfai:usage-updated"));
            return json;
        } catch (e) {
            setError("Failed to generate form");
            setLoading(false);
            return null;
        }
    };

    const getAllForms = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/forms`, {
                headers: {
                    Authorization: `Bearer ${user}`,
                },
            });
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                setLoading(false);
                return [];
            }
            setLoading(false);
            return json;
        } catch (e) {
            setError("Failed to fetch forms");
            setLoading(false);
            return [];
        }
    };

    const getForm = async (formId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/forms/${formId}`, {
                headers: {
                    Authorization: `Bearer ${user}`,
                },
            });
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                setLoading(false);
                return null;
            }
            setLoading(false);
            return json;
        } catch (e) {
            setError("Failed to fetch form");
            setLoading(false);
            return null;
        }
    };

    const updateForm = async (formId, data) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/forms/${formId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user}`,
                },
                body: JSON.stringify(data),
            });
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                return null;
            }
            return json;
        } catch (e) {
            setError("Failed to update form");
            return null;
        }
    };

    const deleteForm = async (formId) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/forms/${formId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${user}`,
                },
            });
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                return false;
            }
            return true;
        } catch (e) {
            setError("Failed to delete form");
            return false;
        }
    };

    const getResponses = async (formId, page = 1, filters = {}) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page });
            if (filters.sentiment) params.set("sentiment", filters.sentiment);
            if (filters.intent) params.set("intent", filters.intent);
            if (filters.leadScoreMin) params.set("leadScoreMin", filters.leadScoreMin);
            if (filters.leadScoreMax) params.set("leadScoreMax", filters.leadScoreMax);

            const response = await fetch(
                `${API_URL}/api/forms/${formId}/responses?${params.toString()}`,
                { headers: { Authorization: `Bearer ${user}` } }
            );
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                setLoading(false);
                return null;
            }
            setLoading(false);
            return json;
        } catch (e) {
            setError("Failed to fetch responses");
            setLoading(false);
            return null;
        }
    };

    const deleteResponse = async (formId, responseId) => {
        setError(null);
        try {
            const response = await fetch(
                `${API_URL}/api/forms/${formId}/responses/${responseId}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${user}`,
                    },
                }
            );
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                return false;
            }
            return true;
        } catch (e) {
            setError("Failed to delete response");
            return false;
        }
    };

    const getPublicForm = async (formId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/f/${formId}`);
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                setLoading(false);
                return null;
            }
            setLoading(false);
            return json;
        } catch (e) {
            setError("Form not found");
            setLoading(false);
            return null;
        }
    };

    const submitPublicForm = async (formId, responseData, captchaToken) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/f/${formId}/submit`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ responseData, captchaToken }),
            });
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                setLoading(false);
                return null;
            }
            setLoading(false);
            return json;
        } catch (e) {
            setError("Failed to submit form");
            setLoading(false);
            return null;
        }
    };

    const savePartialProgress = async (formId, { responseData, currentFieldName, score }) => {
        try {
            await fetch(`${API_URL}/f/${formId}/partial`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ responseData, currentFieldName, score }),
            });
        } catch (_) {}
    };

    const loadPartialProgress = async (formId) => {
        try {
            const response = await fetch(`${API_URL}/f/${formId}/partial`, { credentials: "include" });
            if (!response.ok) return null;
            const json = await response.json();
            return json.partial || null;
        } catch (_) {
            return null;
        }
    };

    const getSettings = async () => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/form-settings`, {
                headers: { Authorization: `Bearer ${user}` },
            });
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                return null;
            }
            return json;
        } catch (e) {
            setError("Failed to fetch settings");
            return null;
        }
    };

    const updateSettings = async (data) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/form-settings`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${user}`,
                },
                body: JSON.stringify(data),
            });
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                return null;
            }
            return json;
        } catch (e) {
            setError("Failed to update settings");
            return null;
        }
    };

    const setCustomDomain = async (domain) => {
        setError(null);
        try {
            const response = await fetch(
                `${API_URL}/api/form-settings/domain`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${user}`,
                    },
                    body: JSON.stringify({ domain }),
                }
            );
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                return null;
            }
            return json;
        } catch (e) {
            setError("Failed to set custom domain");
            return null;
        }
    };

    const verifyCustomDomain = async () => {
        setError(null);
        try {
            const response = await fetch(
                `${API_URL}/api/form-settings/domain/verify`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${user}`,
                    },
                }
            );
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                return null;
            }
            return json;
        } catch (e) {
            setError("Failed to verify domain");
            return null;
        }
    };

    const removeCustomDomain = async () => {
        setError(null);
        try {
            const response = await fetch(
                `${API_URL}/api/form-settings/domain`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${user}`,
                    },
                }
            );
            const json = await response.json();
            if (!response.ok) {
                setError(json.error);
                return null;
            }
            return json;
        } catch (e) {
            setError("Failed to remove custom domain");
            return null;
        }
    };

    const getIntelligenceSettings = async (formId) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/forms/${formId}/intelligence/settings`, {
                headers: { Authorization: `Bearer ${user}` },
            });
            const json = await response.json();
            if (!response.ok) { setError(json.error); return null; }
            return json;
        } catch (e) { setError("Failed to fetch intelligence settings"); return null; }
    };

    const updateIntelligenceSettings = async (formId, data) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/forms/${formId}/intelligence/settings`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${user}` },
                body: JSON.stringify(data),
            });
            const json = await response.json();
            if (!response.ok) { setError(json.error); return null; }
            return json;
        } catch (e) { setError("Failed to update intelligence settings"); return null; }
    };

    const getSubmissionAnalysis = async (submissionId) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/submissions/${submissionId}/analysis`, {
                headers: { Authorization: `Bearer ${user}` },
            });
            const json = await response.json();
            if (!response.ok) { setError(json.error); return null; }
            return json;
        } catch (e) { setError("Failed to fetch analysis"); return null; }
    };

    const reanalyzeSubmission = async (submissionId) => {
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/submissions/${submissionId}/reanalyze`, {
                method: "POST",
                headers: { Authorization: `Bearer ${user}` },
            });
            const json = await response.json();
            if (!response.ok) { setError(json.error); return null; }
            return json;
        } catch (e) { setError("Failed to queue re-analysis"); return null; }
    };

    const getFormInsights = async (formId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_URL}/api/forms/${formId}/insights`, {
                headers: { Authorization: `Bearer ${user}` },
            });
            const json = await response.json();
            if (!response.ok) { setError(json.error); setLoading(false); return null; }
            setLoading(false);
            return json;
        } catch (e) { setError("Failed to fetch insights"); setLoading(false); return null; }
    };

    return {
        generateForm,
        getAllForms,
        getForm,
        updateForm,
        deleteForm,
        getResponses,
        deleteResponse,
        getPublicForm,
        submitPublicForm,
        savePartialProgress,
        loadPartialProgress,
        getSettings,
        updateSettings,
        setCustomDomain,
        verifyCustomDomain,
        removeCustomDomain,
        getIntelligenceSettings,
        updateIntelligenceSettings,
        getSubmissionAnalysis,
        reanalyzeSubmission,
        getFormInsights,
        loading,
        error,
    };
};
